/**
 * Panel de administración — gestión de usuarios.
 *
 * La edición de contenido (secciones, subsecciones, artículos) se hace inline
 * desde cada página con el toolbar de edición, así que aquí solo manejamos
 * usuarios: crear, editar, activar/desactivar, asignar/resetear contraseña.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import {
  Users,
  ShieldCheck,
  Plus,
  KeyRound,
  Pencil,
  Power,
  Loader2,
  Search,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/features/auth/AuthProvider';
import {
  type AdminUser,
  useAdminUsers,
  useCreateUser,
  useDeactivateUser,
  useSetUserPassword,
  useUpdateUser,
} from '@/features/admin/queries';
import { initials, timeAgo, cn } from '@/lib/utils';

function AdminPage() {
  const { user } = useAuth();
  if (user?.rol !== 'admin') {
    return (
      <div className="bg-destructive/10 text-destructive rounded-lg border p-6 text-center">
        Acceso restringido. Esta sección es solo para administradores.
      </div>
    );
  }
  return <AdminContent />;
}

function AdminContent() {
  const { user } = useAuth();
  const { data, isLoading } = useAdminUsers();
  const [filter, setFilter] = useState('');
  const [newUserOpen, setNewUserOpen] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [resetting, setResetting] = useState<AdminUser | null>(null);

  const users = (data?.users ?? []).filter((u) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return u.nombre.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  });

  const adminCount = (data?.users ?? []).filter((u) => u.rol === 'admin' && u.activo).length;
  const lectorCount = (data?.users ?? []).filter((u) => u.rol === 'lector' && u.activo).length;
  const inactivoCount = (data?.users ?? []).filter((u) => !u.activo).length;

  return (
    <div className="space-y-6">
      <header>
        <div className="text-muted-foreground mb-1 flex items-center gap-1.5 text-xs uppercase tracking-wide">
          <ShieldCheck className="h-3.5 w-3.5" /> Panel de administración
        </div>
        <h1 className="text-3xl font-bold tracking-tight">Usuarios</h1>
        <p className="text-muted-foreground mt-1 max-w-2xl text-sm">
          Gestiona quién tiene acceso, sus permisos y sus contraseñas. La edición de contenido
          (secciones, subsecciones, artículos) se hace inline desde cada página con el botón
          “Editar página”.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <StatCard label="Administradores" value={adminCount} />
        <StatCard label="Lectores" value={lectorCount} />
        <StatCard label="Inactivos" value={inactivoCount} muted />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground absolute left-2.5 top-2.5 h-4 w-4" />
          <Input
            placeholder="Buscar por nombre o email…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="pl-8"
          />
        </div>
        <Button variant="brand" onClick={() => setNewUserOpen(true)}>
          <Plus className="mr-1.5 h-4 w-4" />
          Nuevo usuario
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : users.length === 0 ? (
            <div className="text-muted-foreground p-8 text-center text-sm">
              Sin usuarios que coincidan.
            </div>
          ) : (
            <ul className="divide-y">
              {users.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  isMe={u.id === user?.id}
                  onEdit={() => setEditing(u)}
                  onResetPassword={() => setResetting(u)}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <NewUserDialog open={newUserOpen} onOpenChange={setNewUserOpen} />
      <EditUserDialog
        user={editing}
        onClose={() => setEditing(null)}
        isMe={editing?.id === user?.id}
      />
      <ResetPasswordDialog user={resetting} onClose={() => setResetting(null)} />
    </div>
  );
}

function StatCard({ label, value, muted }: { label: string; value: number; muted?: boolean }) {
  return (
    <Card>
      <CardContent className="flex items-center justify-between py-4">
        <div>
          <div className="text-muted-foreground text-xs uppercase tracking-wide">{label}</div>
          <div className={cn('mt-1 text-2xl font-bold', muted && 'text-muted-foreground')}>
            {value}
          </div>
        </div>
        <Users className={cn('h-6 w-6', muted ? 'text-muted-foreground/40' : 'text-brand')} />
      </CardContent>
    </Card>
  );
}

function UserRow({
  user,
  isMe,
  onEdit,
  onResetPassword,
}: {
  user: AdminUser;
  isMe: boolean;
  onEdit: () => void;
  onResetPassword: () => void;
}) {
  const deactivate = useDeactivateUser();
  const update = useUpdateUser();

  const toggleActivo = async () => {
    if (isMe) return;
    if (user.activo) {
      if (!confirm(`¿Desactivar a ${user.nombre}? No podrá iniciar sesión.`)) return;
      await deactivate.mutateAsync(user.id);
    } else {
      await update.mutateAsync({ id: user.id, data: { activo: true } });
    }
  };

  return (
    <li
      className={cn(
        'flex items-center gap-3 px-4 py-3 transition-colors',
        !user.activo && 'opacity-60',
      )}
    >
      <Avatar className="h-9 w-9">
        <AvatarFallback>{initials(user.nombre)}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{user.nombre}</span>
          {isMe && <Badge variant="outline" className="h-5">tú</Badge>}
        </div>
        <div className="text-muted-foreground truncate text-xs">
          {user.email} · creado {timeAgo(user.createdAt)}
        </div>
      </div>

      <Badge variant={user.rol === 'admin' ? 'brand' : 'secondary'} className="hidden md:inline-flex">
        {user.rol === 'admin' ? 'Administrador' : 'Lector'}
      </Badge>
      <Badge variant={user.tienePassword ? 'success' : 'outline'} className="hidden lg:inline-flex">
        <KeyRound className="mr-1 h-3 w-3" />
        {user.tienePassword ? 'Con contraseña' : 'Sin contraseña'}
      </Badge>
      {!user.activo && <Badge variant="outline">Inactivo</Badge>}

      <div className="flex items-center gap-1">
        <Button size="sm" variant="ghost" onClick={onEdit} title="Editar">
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button size="sm" variant="ghost" onClick={onResetPassword} title="Asignar contraseña">
          <KeyRound className="h-3.5 w-3.5" />
        </Button>
        {!isMe && (
          <Button
            size="sm"
            variant="ghost"
            onClick={toggleActivo}
            title={user.activo ? 'Desactivar' : 'Activar'}
            className={user.activo ? 'text-destructive hover:text-destructive' : ''}
          >
            <Power className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </li>
  );
}

// ----------------------- Dialogs -----------------------

function NewUserDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const create = useCreateUser();
  const [form, setForm] = useState({
    email: '',
    nombre: '',
    rol: 'lector' as 'admin' | 'lector',
    password: '',
  });
  const [err, setErr] = useState<string | null>(null);

  const reset = () => {
    setForm({ email: '', nombre: '', rol: 'lector', password: '' });
    setErr(null);
  };

  const submit = async () => {
    setErr(null);
    if (form.password.length < 8) {
      setErr('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    try {
      await create.mutateAsync({
        email: form.email.trim(),
        nombre: form.nombre.trim(),
        rol: form.rol,
        password: form.password,
      });
      reset();
      onOpenChange(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'No se pudo crear');
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nuevo usuario</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Nombre">
            <Input
              autoFocus
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              placeholder="Juan Pérez"
            />
          </Field>
          <Field label="Email">
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="juan@repuestosespana.cl"
            />
          </Field>
          <Field label="Rol">
            <RoleSelect value={form.rol} onChange={(v) => setForm({ ...form, rol: v })} />
          </Field>
          <Field label="Contraseña inicial" hint="Mínimo 8 caracteres. El usuario la usará para iniciar sesión.">
            <Input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="••••••••"
            />
          </Field>
          {err && (
            <p className="text-destructive bg-destructive/10 rounded-md px-3 py-2 text-xs">{err}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={submit}
            disabled={create.isPending || !form.email || !form.nombre || form.password.length < 8}
          >
            {create.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Crear usuario
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({
  user,
  isMe,
  onClose,
}: {
  user: AdminUser | null;
  isMe: boolean;
  onClose: () => void;
}) {
  const update = useUpdateUser();
  const [form, setForm] = useState({ nombre: '', email: '', rol: 'lector' as 'admin' | 'lector' });

  useEffect(() => {
    if (user) setForm({ nombre: user.nombre, email: user.email, rol: user.rol });
  }, [user]);

  const submit = async () => {
    if (!user) return;
    await update.mutateAsync({
      id: user.id,
      data: {
        nombre: form.nombre,
        email: form.email,
        rol: form.rol,
      },
    });
    onClose();
  };

  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Editar usuario</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Nombre">
            <Input
              value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })}
            />
          </Field>
          <Field label="Email">
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>
          <Field label="Rol" hint={isMe ? 'No puedes cambiar tu propio rol.' : undefined}>
            <RoleSelect
              value={form.rol}
              onChange={(v) => setForm({ ...form, rol: v })}
              disabled={isMe}
            />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={update.isPending}>
            {update.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({
  user,
  onClose,
}: {
  user: AdminUser | null;
  onClose: () => void;
}) {
  const setPwd = useSetUserPassword();
  const [pwd, setPwdValue] = useState('');
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setPwdValue('');
      setErr(null);
    }
  }, [user]);

  const submit = async (clear = false) => {
    if (!user) return;
    setErr(null);
    try {
      await setPwd.mutateAsync({ id: user.id, password: clear ? null : pwd });
      setPwdValue('');
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Error');
    }
  };

  return (
    <Dialog
      open={!!user}
      onOpenChange={(v) => {
        if (!v) {
          setPwdValue('');
          setErr(null);
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Contraseña de {user?.nombre}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-muted-foreground text-sm">
            {user?.tienePassword
              ? 'Esta cuenta tiene contraseña. Puedes asignar una nueva o quitarla para volver al login solo con email.'
              : 'Esta cuenta hoy entra solo con email. Asígnale una contraseña para protegerla.'}
          </p>
          <Field label="Nueva contraseña" hint="Mínimo 8 caracteres.">
            <Input
              type="password"
              autoFocus
              value={pwd}
              onChange={(e) => setPwdValue(e.target.value)}
              placeholder="••••••••"
            />
          </Field>
          {err && (
            <p className="text-destructive bg-destructive/10 rounded-md px-3 py-2 text-xs">{err}</p>
          )}
        </div>
        <DialogFooter>
          {user?.tienePassword && (
            <Button
              variant="ghost"
              className="text-destructive hover:text-destructive mr-auto"
              onClick={() => {
                if (confirm('¿Quitar contraseña? La cuenta volverá a login solo con email.')) {
                  submit(true);
                }
              }}
              disabled={setPwd.isPending}
            >
              Quitar contraseña
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => submit(false)} disabled={setPwd.isPending || pwd.length < 8}>
            {setPwd.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Asignar contraseña
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----------------------- UI helpers -----------------------

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{label}</label>
      {children}
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
  );
}

function RoleSelect({
  value,
  onChange,
  disabled,
}: {
  value: 'admin' | 'lector';
  onChange: (v: 'admin' | 'lector') => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex gap-2">
      {(['lector', 'admin'] as const).map((r) => (
        <button
          key={r}
          type="button"
          disabled={disabled}
          onClick={() => onChange(r)}
          className={cn(
            'flex-1 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
            value === r
              ? 'bg-brand/15 text-brand border-brand/30'
              : 'border-input text-muted-foreground hover:bg-accent',
          )}
        >
          {r === 'admin' ? 'Administrador' : 'Lector'}
        </button>
      ))}
    </div>
  );
}

export const Route = createFileRoute('/admin')({ component: AdminPage });
